import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import type { StatementItemReference } from "@/payroll/ai-script/statement/statement-renderer";
import { useStartPackThread } from "@/payroll/pack-chat/query";
import { GENERAL_PAYPACK_CONTEXT } from "@/payroll/pack-chat/reference-label";
import { AttachedStatementItem } from "./attached-statement-item";
import { PACK_QUESTION_DESCRIPTION } from "./pack-question-copy";

const MAX_FEEDBACK_LENGTH = 1500;
const MIN_FEEDBACK_LENGTH = 10;

interface PackQuestionFormProps {
  businessId: string | null;
  /** The run this pack came from — feedback is about these numbers, not the period. */
  runId: string;
  /**
   * The figure the technician tapped in the statement, if any. Without one, the
   * thread is about the PayPack as a whole. A selected figure comes from the pack
   * itself rather than a long list of rows that make no sense out of context.
   */
  attached: StatementItemReference | null;
  onClearAttached: () => void;
  /** Called once the message is stored, so a drawer can close itself. */
  onSent?: (threadId: string) => void;
  /** Put the cursor in the box on mount — for when the form opened on purpose. */
  autoFocus?: boolean;
  /** Rendered above the box; the drawer supplies its own title instead. */
  heading?: boolean;
  aiEnabled?: boolean;
}

/**
 * Raising feedback about this pack.
 *
 * Posts to the pack-scoped endpoint. It used to post to `tech_feedbacks` — the
 * unauthenticated marketing contact form — which dropped the message into Slack
 * with the pay period jammed into free text, attached to no business, period or
 * run, and with no way for a manager to see or close it.
 */
export const PackQuestionForm = ({
  businessId,
  runId,
  attached,
  onClearAttached,
  onSent,
  autoFocus,
  heading = true,
  aiEnabled = false,
}: PackQuestionFormProps) => {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const startThread = useStartPackThread(businessId);
  const isSubmittingRef = useRef(false);

  // The drawer opens in response to a tap, so the technician is already
  // committed — landing them in the box saves a step rather than stealing focus.
  useEffect(() => {
    if (autoFocus) bodyRef.current?.focus();
  }, [autoFocus]);

  // Tapping a figure should also land them in the box: the point of attaching
  // from the statement is that it saves a step, not adds one.
  useEffect(() => {
    if (attached) bodyRef.current?.focus();
  }, [attached]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // A ref, not `startThread.isPending`. The control stays interactive across the
    // await, and `isPending` is render state — two submits dispatched before React
    // re-renders both read the same stale `false` and both posted, so the manager
    // saw two identical questions. A ref is written synchronously, so the second
    // event sees the lock the first one took in the same tick.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      const trimmed = body.trim();

      if (trimmed.length < MIN_FEEDBACK_LENGTH) {
        setError(
          `Please describe the issue in at least ${MIN_FEEDBACK_LENGTH} characters.`,
        );
        return;
      }
      if (trimmed.length > MAX_FEEDBACK_LENGTH) {
        setError("This is too long. Please shorten your message.");
        return;
      }
      setError(null);

      try {
        const { thread } = await startThread.mutateAsync({
          runId,
          body: trimmed,
          context: attached ?? GENERAL_PAYPACK_CONTEXT,
        });
        toast.success(aiEnabled ? "Question sent" : "Sent to your manager", {
          description: aiEnabled
            ? "AI will answer first. You can request a manager in the conversation."
            : "They'll take a look and get back to you.",
        });
        setBody("");
        onClearAttached();
        onSent?.(thread.id);
      } catch (submitError) {
        toast.error("Couldn't send that", {
          description: getErrorMessage(submitError),
        });
      }
    } finally {
      // In `finally` so the early returns above release it too: a message rejected
      // for being too short must still be sendable after it is fixed.
      isSubmittingRef.current = false;
    }
  };

  const title = heading ? (
    <Stack className="w-full" align="start" gap={1}>
      <Text variant="title">Something look wrong?</Text>
      <Text variant="caption" tone="muted">
        {aiEnabled
          ? PACK_QUESTION_DESCRIPTION.ai
          : PACK_QUESTION_DESCRIPTION.manager}
      </Text>
    </Stack>
  ) : null;

  return (
    // w-full because the enclosing Stack is items-start: without it the form
    // shrinks to its content and the box and button sit narrower than the text
    // above them.
    <form onSubmit={handleSubmit} className="w-full">
      <Stack className="w-full" align="start" gap={2}>
        {title}

        {attached && (
          <AttachedStatementItem item={attached} onRemove={onClearAttached} />
        )}

        <Textarea
          ref={bodyRef}
          // No visible label: the heading above already says what this is, and
          // the label carried a second required asterisk.
          aria-label="What looks wrong"
          value={body}
          placeholder="Describe what seems incorrect or missing"
          disabled={startThread.isPending}
          className="min-h-24 resize-y pt-2"
          onChange={(event) => {
            setBody(event.target.value);
            if (error) setError(null);
          }}
        />
        {error && (
          <Text variant="caption" className="text-danger">
            {error}
          </Text>
        )}
        <Button
          type="submit"
          disabled={!body.trim() || startThread.isPending}
          className="w-full"
          aria-busy={startThread.isPending}
        >
          {startThread.isPending && (
            <Spinner aria-hidden data-icon="inline-start" />
          )}
          {aiEnabled ? "Send question" : "Send to my manager"}
        </Button>
      </Stack>
    </form>
  );
};
